import { useCallback, useMemo, useState } from 'react'
import { Ban, Plus, RefreshCw } from 'lucide-react'

import { DataTable, type DataTableColumn } from '@/admin/components/data-table'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { useApiKeys } from '@/admin/hooks/use-api-keys'
import { useFormatting } from '@/admin/hooks/use-formatting'
import type { ApiKey, ApiKeyCreated } from '@/admin/types/api-keys'
import { CreateApiKeyDialog } from './create-api-key-dialog'
import { RevealApiKeyDialog } from './reveal-api-key-dialog'
import { RevokeApiKeyDialog } from './revoke-api-key-dialog'
import { RotateApiKeyDialog } from './rotate-api-key-dialog'

export function ApiKeysTab() {
  const { data: keys, isLoading, invalidate } = useApiKeys()
  const { formatDate: fmtDate } = useFormatting()
  const formatDate = useCallback(
    (iso: string | null): string => (iso ? fmtDate(iso) : '-'),
    [fmtDate],
  )

  // Dialog state
  const [createOpen, setCreateOpen] = useState(false)
  const [revealKey, setRevealKey] = useState<ApiKey | null>(null)
  const [revealRawToken, setRevealRawToken] = useState<string | undefined>(undefined)
  const [revokeKey, setRevokeKey] = useState<ApiKey | null>(null)
  const [rotateKey, setRotateKey] = useState<ApiKey | null>(null)

  function showRawTokenOnce(created: ApiKeyCreated) {
    // The full secret is shown exactly once (creation or rotation).
    setRevealKey(created)
    setRevealRawToken(created.raw_token)
    invalidate()
  }

  function handleCreated(created: ApiKeyCreated) {
    setCreateOpen(false)
    showRawTokenOnce(created)
  }

  function handleRotated(created: ApiKeyCreated) {
    setRotateKey(null)
    showRawTokenOnce(created)
  }

  function handleRevealClose() {
    setRevealKey(null)
    setRevealRawToken(undefined)
  }

  function handleRevoked() {
    invalidate()
  }

  const columns: DataTableColumn<ApiKey>[] = useMemo(
    () => [
      {
        id: 'name',
        header: 'Name',
        cell: (row) => (
          <span className="text-[13px] font-medium text-[var(--ink)]">{row.name}</span>
        ),
      },
      {
        id: 'masked_token',
        header: 'Token',
        cell: (row) => (
          <code className="text-[12px] font-mono text-[var(--ink-3)]">
            {row.masked_display}
          </code>
        ),
      },
      {
        id: 'scope',
        header: 'Scope',
        cell: (row) => {
          const isPartner =
            row.scope === 'partner' || row.scope === 'partner_read_only'
          if (isPartner && row.scoped_partnership) {
            const label =
              row.scope === 'partner_read_only' ? 'Partner (read-only)' : 'Partner'
            return (
              <Badge variant="secondary">
                {label}: {row.scoped_partnership.name}
              </Badge>
            )
          }
          return <Badge variant="outline">Full access</Badge>
        },
      },
      {
        id: 'minted_by',
        header: 'Created by',
        cell: (row) => (
          <span className="text-[12px] text-[var(--ink-3)]">
            {row.minted_by?.name ?? row.minted_by?.email ?? '-'}
          </span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        cell: (row) =>
          row.revoked_at ? (
            <Badge variant="destructive">Revoked</Badge>
          ) : (
            <Badge
              variant="outline"
              className="border-[var(--status-ok-border)] bg-[var(--status-ok-bg)] text-[var(--status-ok)]"
            >
              Active
            </Badge>
          ),
      },
      {
        id: 'created_at',
        header: 'Created',
        cell: (row) => (
          <span className="text-[12px] text-[var(--ink-3)]">{formatDate(row.created_at)}</span>
        ),
      },
      {
        id: 'last_used_at',
        header: 'Last Used',
        cell: (row) => (
          <span className="text-[12px] text-[var(--ink-3)]">{formatDate(row.last_used_at)}</span>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: (row) =>
          !row.revoked_at ? (
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon-xs"
                onClick={(e) => {
                  e.stopPropagation()
                  setRotateKey(row)
                }}
                title="Rotate key"
              >
                <RefreshCw className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="icon-xs"
                onClick={(e) => {
                  e.stopPropagation()
                  setRevokeKey(row)
                }}
                title="Revoke key"
              >
                <Ban className="h-3.5 w-3.5 text-[var(--status-error)]" />
              </Button>
            </div>
          ) : null,
      },
    ],
    [formatDate],
  )

  return (
    <div className="mt-8">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-[15px] font-medium text-[var(--ink)]">API Keys</h2>
          <p className="mt-0.5 text-[12px] text-[var(--ink-3)]">
            Manage bearer tokens for the public API. The full key is shown once, at creation; rotate
            to issue a new secret.
          </p>
        </div>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="mr-1.5 h-3 w-3" />
          Create Key
        </Button>
      </div>

      {/* Table */}
      <DataTable<ApiKey>
        tableId="api-keys"
        columns={columns}
        data={keys ?? []}
        isLoading={isLoading}
        rowKey={(row) => row.uuid}
      />

      {/* Dialogs */}
      <CreateApiKeyDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={handleCreated}
      />

      <RevealApiKeyDialog
        open={!!revealKey}
        onClose={handleRevealClose}
        apiKey={revealKey}
        rawToken={revealRawToken}
      />

      <RevokeApiKeyDialog
        open={!!revokeKey}
        onClose={() => setRevokeKey(null)}
        apiKey={revokeKey}
        onRevoked={handleRevoked}
      />

      <RotateApiKeyDialog
        open={!!rotateKey}
        onClose={() => setRotateKey(null)}
        apiKey={rotateKey}
        onRotated={handleRotated}
      />
    </div>
  )
}
